import { Icon } from "@indigo-components";
import type { FocusEvent, KeyboardEvent, ReactNode } from "react";
import { TextField as DesignSystemTextField } from "~/indigo-components/src/components/TextField";
import type { BaseBlockProps } from "./Block";

export type TextAreaBlockProps<T extends string | number | null = string> =
  BaseBlockProps & {
    value: T;
    onChange?: (value: string) => void;
    placeholder?: string;
    onKeyDown?: (event: KeyboardEvent) => void;
    onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
    className?: string;
    displayValue?: string;
    isInitiallyFocused?: boolean;
    infoIcon?: ReactNode;
    infoIconTooltip?: string | null;
    widthPercent?: string;
    areaSize?: "default" | "large";
    description?: ReactNode;
    "data-testid"?: string;
    descriptionAction?: {
      label: string;
      onClick: () => void;
      ariaLabel?: string;
    };
    actionIcon?: React.ReactNode;
    actionTooltip?: string;
    onActionClick?: () => void;
    actionAriaLabel?: string;
    isActionDisabled?: boolean;
    isActionActive?: boolean;
    isUnavailable?: boolean;
    isLabelLoading?: boolean;
  };

const TextAreaBlock = <T extends string | number | null = string>({
  value,
  onChange,
  disabled,
  placeholder = disabled ? "-" : "Enter text",
  onKeyDown,
  onBlur,
  className,
  displayValue,
  isInitiallyFocused,
  infoIcon,
  infoIconTooltip,
  widthPercent,
  areaSize = "default",
  description,
  descriptionAction,
  "data-testid": dataTestId,
  actionIcon,
  actionTooltip,
  onActionClick,
  actionAriaLabel,
  isActionDisabled,
  isActionActive,
  copyValue,
  copyTooltip = "Copy",
  copyAriaLabel = "Copy value",
  isUnavailable = false,
  isLabelLoading = false,
  ...additionalProps
}: TextAreaBlockProps<T>) => {
  const defaultCopyAction =
    copyValue && !onActionClick
      ? () => {
          if (typeof navigator === "undefined") return;
          void navigator.clipboard.writeText(copyValue);
        }
      : undefined;

  const resolvedOnActionClick = onActionClick || defaultCopyAction;
  const resolvedActionIcon =
    actionIcon || (copyValue ? <Icon name="copy" size="s" /> : undefined);
  const resolvedActionTooltip =
    actionTooltip || (copyValue ? copyTooltip : undefined);
  const resolvedActionAriaLabel =
    actionAriaLabel || (copyValue ? copyAriaLabel : undefined);

  return (
    <DesignSystemTextField
      value={value}
      onChange={onChange}
      disabled={disabled}
      isUnavailable={isUnavailable}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      className={className}
      displayValue={displayValue}
      isInitiallyFocused={isInitiallyFocused}
      infoIcon={infoIcon}
      infoIconTooltip={infoIconTooltip}
      widthPercent={widthPercent}
      areaSize={areaSize}
      label={additionalProps.label}
      isError={additionalProps.isError}
      description={description}
      isLabelLoading={isLabelLoading}
      descriptionAction={descriptionAction}
      data-testid={dataTestId}
      actionIcon={copyValue && !actionIcon ? undefined : resolvedActionIcon}
      actionTooltip={
        copyValue && !actionIcon ? undefined : resolvedActionTooltip
      }
      onActionClick={
        copyValue && !actionIcon ? undefined : resolvedOnActionClick
      }
      actionAriaLabel={
        copyValue && !actionIcon ? undefined : resolvedActionAriaLabel
      }
      isActionDisabled={isActionDisabled}
      isActionActive={isActionActive}
      copyValue={copyValue ?? undefined}
      {...additionalProps}
    />
  );
};

export default TextAreaBlock;
